/**
 * The "Payment" line on the public private lesson page (#5249).
 *
 * Mirrors the booking routes (`api/private-lessons/[id]/bookings` and its
 * `batch` twin): only `payment_model === 'direct_to_gym'` collects through
 * CheerVue, and within that only `payment_timing === 'pay_on_lesson_day'`
 * defers to invoices: one per lesson, due on its lesson day (#6229)
 * unless a credit pack is bought in the same checkout; every other timing
 * charges at booking. A credit pays instead of either. "Until canceled" weekly
 * series bill on their own, never at booking; 4–10 week blocks are ordinary
 * carts and follow the timing above.
 *
 * Returns null when the page cannot make a true claim: no lesson length has a
 * price (a free lesson is never charged or invoiced, even when packs are
 * priced) or a payment model neither route bills.
 */
export function describePrivateLessonPaymentTiming(params: {
  paymentModel?: string | null;
  paymentTiming?: string | null;
  hasPricedLesson: boolean;
  sellsCreditPacks: boolean;
}): string | null {
  const { paymentModel, paymentTiming, hasPricedLesson, sellsCreditPacks } = params;
  if (!hasPricedLesson) return null;

  if (paymentModel === 'direct_to_coach') return 'Pay your coach directly.';
  if (paymentModel !== 'direct_to_gym') return null;

  const payOnLessonDay = paymentTiming === 'pay_on_lesson_day';
  const whenYouPay = payOnLessonDay
    ? 'Book now. Each lesson is billed on its own, due on the lesson day.'
    : 'Pay online when you book.';
  // A pack in the cart turns a pay-on-lesson-day checkout into a card charge
  // for the whole cart, with no invoice (`bookings/batch/route.ts`).
  const credits = !sellsCreditPacks
    ? ''
    : payOnLessonDay
      ? ' Lesson credits also work. Buying a credit pack? You pay at checkout.'
      : ' Lesson credits also work.';
  return `${whenYouPay}${credits} "Until canceled" weekly lessons are billed separately.`;
}
